import { useState } from "react";

import { MobileHeading } from "features/layout/components";

import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import Loader from "common/components/Loader";
import SearchInput from "common/components/SearchInput";
import { Button } from "common/components/ui/Button";
import useMembers from "common/datahooks/useMembers";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import SettingsScrollContent from "../SettingsScrollContent";
import DesktopTable from "./DesktopLayout/DesktopTable";
import MobileList from "./MobileLayout/MobileList";
import NewClientButton from "./NewClientButton";

import EmptyPlaceholderImage from "assets/images/empty-placeholders/no-webhooks.png";
import ResultNotFoundImage from "assets/images/empty-placeholders/result-not-found.png";

export default function Clients() {
  const isDesktop = useTwBreakpoint("lg");

  const [searchTerm, setSearchTerm] = useState("");

  const { members, isLoadingMembers, isMembersError, refetchMembers } =
    useMembers();

  if (isMembersError) {
    return (
      <ErrorPlaceholder
        errorMessage="Could not display clients"
        onRetry={refetchMembers}
      />
    );
  }
  if (isLoadingMembers) {
    return <Loader />;
  }

  const clients = members?.filter(({ role }) => role === "agency_client");

  if (!clients?.length) {
    return (
      <EmptyPlaceholder
        imageSrc={EmptyPlaceholderImage}
        title="No clients found"
        subtitle="Add a client to get started"
        actionComponent={<NewClientButton size="md" />}
      />
    );
  }

  const lowerCaseSearchTerm = searchTerm.toLowerCase();

  const filteredClients = clients.filter(
    ({ first_name: firstName, last_name: lastName, email }) => {
      const isMatchingFirstName = firstName
        ?.toLowerCase()
        .includes(lowerCaseSearchTerm);

      const isMatchingLastName = lastName
        ?.toLowerCase()
        .includes(lowerCaseSearchTerm);

      const isMatchingEmail = email
        ?.toLowerCase()
        .includes(lowerCaseSearchTerm);

      return searchTerm
        ? isMatchingFirstName || isMatchingLastName || isMatchingEmail
        : true;
    },
  );

  const Component = isDesktop ? DesktopTable : MobileList;

  const actionComponent = <NewClientButton size={isDesktop ? "sm" : "md"} />;

  const searchInputComponent = (
    <SearchInput
      placeholder="Search clients"
      value={searchTerm}
      className="lg:max-w-72"
      variant={isDesktop ? "sm" : "lg"}
      onChange={(e) => setSearchTerm(e.target.value)}
      onClear={() => setSearchTerm("")}
    />
  );

  return (
    <SettingsScrollContent desktopActionComponent={actionComponent}>
      <MobileHeading
        actionComponent={actionComponent}
        searchComponent={searchInputComponent}
      />
      {isDesktop && searchInputComponent}
      {filteredClients.length ? (
        <Component clients={filteredClients} />
      ) : (
        <EmptyPlaceholder
          title="No clients found"
          subtitle="No clients match your search criteria. Try adjusting your search"
          imageSrc={ResultNotFoundImage}
          actionComponent={
            <Button onClick={() => setSearchTerm("")}>Clear search</Button>
          }
        />
      )}
    </SettingsScrollContent>
  );
}
